import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import './App.css';
import { initGA, referralTracker, analytics } from './analytics';
import { initPixel } from './facebook-pixel';
import { initPersonalization, getInboundSignal, getOrCreateUID } from './utils/personalization';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import FeedbackButton from './components/FeedbackButton';
import AppDownloadStickyBanner from './components/AppDownloadStickyBanner';

// Lazy-loaded routes (keeps blog-posts.js & deal data out of main bundle)
const DealZone = lazy(() => import('./pages/DealZone'));
const HubPage = lazy(() => import('./pages/HubPage'));
const BlogIndex = lazy(() => import('./components/BlogIndex'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const SharedResults = lazy(() => import('./pages/SharedResults'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const GiftTrendsPage = lazy(() => import('./pages/GiftTrendsPage'));
const AiGiftFinderPage = lazy(() => import('./pages/AiGiftFinderPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

function NotFoundPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center p-4">
      <Helmet>
        <title>Page Not Found | Pickify</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="text-center max-w-md">
        <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Page Not Found</h2>
        <p className="text-gray-600 mb-8">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/"
          className="inline-block px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-lg rounded-full hover:shadow-xl transition-all transform hover:scale-105"
        >
          Back to Home
        </Link>
      </div>
    </main>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  // Initialize analytics & tracking — defer non-critical work to unblock LCP
  useEffect(() => {
    // Capture referral params immediately (lightweight, no network)
    referralTracker.captureReferral();

    // Track visit source (referrer) to DB — only once per session (skip admin)
    if (!sessionStorage.getItem('pickify_visit_tracked') && window.location.pathname !== '/admin') {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = sessionStorage.getItem('pickify_session_id') ||
        `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('pickify_session_id', sessionId);

      fetch('/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'visit',
          sessionId,
          uid: getOrCreateUID(),
          referrer: document.referrer || null,
          utmSource: urlParams.get('utm_source') || null,
          utmMedium: urlParams.get('utm_medium') || null,
          utmCampaign: urlParams.get('utm_campaign') || null,
          landingPage: window.location.pathname,
          countryCode: null, // filled by API from IP
        }),
      }).catch(() => {}); // fire-and-forget
      sessionStorage.setItem('pickify_visit_tracked', '1');
    }

    // Defer all analytics loading until first user interaction or 4s timeout
    // This keeps GA4 + react-ga4 out of the critical path entirely
    let loaded = false;
    const loadAnalytics = () => {
      if (loaded) return;
      loaded = true;
      // Skip all analytics on admin page
      if (window.location.pathname === '/admin') return;
      initGA();
      initPixel();
      initPersonalization();
      const signal = getInboundSignal();
      if (signal && signal.category) {
        analytics.inboundSignalCaptured(signal.category, signal.source, signal.campaign);
      }
      // Clean up listeners
      ['scroll', 'click', 'touchstart', 'keydown'].forEach(e =>
        window.removeEventListener(e, loadAnalytics, { capture: true })
      );
    };

    ['scroll', 'click', 'touchstart', 'keydown'].forEach(e =>
      window.addEventListener(e, loadAnalytics, { once: true, capture: true, passive: true })
    );
    // Fallback: load after 4s even without interaction (for bounce tracking)
    const timer = setTimeout(loadAnalytics, 4000);

    return () => {
      clearTimeout(timer);
      ['scroll', 'click', 'touchstart', 'keydown'].forEach(e =>
        window.removeEventListener(e, loadAnalytics, { capture: true })
      );
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Header />
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div></div>}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/deals" element={<DealZone />} />
              <Route path="/gifts/:category" element={<HubPage />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/r/:id" element={<SharedResults />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/knowledge-base/:slug" element={<KnowledgeBasePage />} />
              <Route path="/insights/ai-gift-trends-2026" element={<GiftTrendsPage />} />
              <Route path="/ai-gift-finder" element={<AiGiftFinderPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <Footer />
          <FeedbackButton />
          <AppDownloadStickyBanner />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
